import {useEffect, useState} from 'react';
import {patientService} from '../../services/patientService';
import {ehrService} from '../../services/ehrService';

// ── useRecentPatients ─────────────────────────────────────────────
//
// Loads the last N unique patients the user has actually had a session
// with. The backend recents endpoint (GET /api/v2/patients/recent, via
// patientService.fetchRecentPatientsForUser) returns hydrated local
// Patient docs; only the EHR-cache twin still needs a client-side
// hydration pass for EHR-linked rows.
//
// Returned items follow the same shape as `buildUnifiedPatientList`
// produces (so they slot into existing picker rendering paths) plus a
// `lastSessionAt` field for sorting / display:
//
//   {
//     id: 'recent_<key>',
//     localPatient: <Patient doc> | null,
//     ehrPatient: <mapped EHR patient> | null,
//     matchKind: 'recent',
//     displayName: '...',
//     sortName: '...',
//     lastSessionAt: Date | null,
//   }
//
// Skips silently when `userId` is missing or `enabled === false` (e.g.
// non-EHR companies — callers gate on `hasActiveEhr`). Already-deduped
// items are returned in `lastSessionAt desc` order.
export default function useRecentPatients({
  userId,
  companyId,
  enabled = true,
  limitCount = 10,
}: Record<string, any>) {
  const [recentPatients, setRecentPatients] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !userId) {
      setRecentPatients([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const recents = await patientService.fetchRecentPatientsForUser(
          userId,
          {
            companyId,
            limitCount,
          },
        );
        if (cancelled) return;
        if (!recents.length) {
          setRecentPatients([]);
          return;
        }

        const ehrIds = [
          ...new Set(recents.map(r => r.ehrPatientId).filter(Boolean)),
        ];

        // Local docs arrive hydrated on the recents rows; only the EHR-cache
        // twins still need a lookup for linked rows.
        const ehrById: any =
          ehrIds.length && companyId
            ? await ehrService.fetchCachedPatientsByIds(companyId, ehrIds)
            : {};

        if (cancelled) return;

        const items: any[] = [];
        for (const r of recents) {
          const localPatient = (r as any).patient || null;
          const ehrPatient = r.ehrPatientId
            ? ehrById[r.ehrPatientId] || null
            : null;

          // Drop entries we couldn't hydrate at all — they'd render as
          // "Unknown" rows, which is worse than just hiding them. The
          // `patientName` snapshot from the Conversation could be used as
          // a fallback in the future.
          if (!localPatient && !ehrPatient) continue;

          const firstName =
            localPatient?.first_name || ehrPatient?.firstName || '';
          const lastName =
            localPatient?.last_name || ehrPatient?.lastName || '';
          const display = lastName
            ? `${lastName}, ${firstName}`
            : firstName || r.patientName || 'Unknown';

          items.push({
            id: `recent_${r.ehrPatientId || ''}_${r.patientId || ''}`,
            localPatient,
            ehrPatient,
            matchKind: 'recent',
            displayName: display,
            sortName: (lastName || firstName || '').toLowerCase(),
            lastSessionAt: r.lastSessionAt,
          });
        }

        setRecentPatients(items);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('useRecentPatients failed:', err?.message);
        }
        if (!cancelled) setRecentPatients([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, companyId, enabled, limitCount]);

  return {recentPatients, loading};
}
